import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BUSINESS } from '../../mocks/sharedData';

const NAV_ITEMS = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'About', href: '/about' },
  { label: 'Locations', href: '/locations' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/98 backdrop-blur-xl border-b border-zinc-100 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <div className="w-11 h-11 flex items-center justify-center rounded-full overflow-hidden bg-white/10">
            <img
              src={BUSINESS.logo}
              alt={BUSINESS.name}
              className="w-10 h-10 object-contain"
            />
          </div>
          <div>
            <span
              className={`font-serif font-semibold text-lg leading-none block transition-colors duration-300 ${
                scrolled ? 'text-zinc-900' : 'text-white'
              }`}
            >
              Star Solar
            </span>
            <span
              className={`text-[10px] tracking-[0.2em] uppercase font-medium transition-colors duration-300 ${
                scrolled ? 'text-amber-600' : 'text-amber-400'
              }`}
            >
              Specialists
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`text-sm tracking-wide font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === item.href
                  ? scrolled ? 'text-amber-600' : 'text-amber-400'
                  : scrolled
                    ? 'text-zinc-600 hover:text-zinc-900'
                    : 'text-white/80 hover:text-white'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-5">
          <a
            href={BUSINESS.phoneTel}
            className={`flex items-center gap-2 text-sm font-semibold whitespace-nowrap transition-colors duration-300 cursor-pointer ${
              scrolled ? 'text-zinc-700 hover:text-amber-600' : 'text-white/90 hover:text-amber-300'
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-phone-fill text-base" />
            </div>
            {BUSINESS.phone}
          </a>
          <a
            href={BUSINESS.consultationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-sm font-bold px-6 py-3 rounded-full whitespace-nowrap transition-all duration-300 cursor-pointer ${
              scrolled
                ? 'bg-amber-500 hover:bg-amber-600 text-white'
                : 'bg-amber-500 hover:bg-amber-400 text-zinc-950'
            }`}
          >
            Free Estimate
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden cursor-pointer p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <i
              className={`text-xl transition-colors duration-300 ${
                menuOpen ? 'ri-close-line' : 'ri-menu-3-line'
              } ${scrolled ? 'text-zinc-900' : 'text-white'}`}
            />
          </div>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          menuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-white border-t border-zinc-100 px-6 pt-4 pb-6">
          <nav className="flex flex-col gap-1 mb-6">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-sm font-medium py-3 border-b border-zinc-50 whitespace-nowrap cursor-pointer transition-colors ${
                  location.pathname === item.href ? 'text-amber-600' : 'text-zinc-700 hover:text-zinc-900'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <a
            href={BUSINESS.phoneTel}
            className="flex items-center gap-2 text-zinc-800 font-semibold text-sm mb-4 whitespace-nowrap cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-phone-fill text-amber-500" />
            </div>
            {BUSINESS.phone}
          </a>
          <a
            href={BUSINESS.consultationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors"
          >
            Get Free Estimate
          </a>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
